import React, { useState, useMemo } from "react";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Table,
  TableBody,
  TableRow,
  TableCell,
  TableHead,
  CircularProgress,
  Alert,
  Chip,
  Select,
  MenuItem,
  FormControl,
  TextField,
} from "@mui/material";
import { SelectChangeEvent } from "@mui/material/Select";
import { SxProps, Theme } from "@mui/material/styles";
import { useDashboardStats } from "../hooks/useDashboardStats";
import {
  usePeriodStats,
  PeriodPreset,
  PERIOD_PRESET_OPTIONS,
  resolveDateRangeFromPreset,
  getTodayDateString,
} from "../hooks/usePeriodStats";
import { TopicStats } from "../types";
import ResponseStatsChart from "./ResponseStatsChart";
import TopicBadges from "./common/TopicBadges";

// 比較チップ（増減率表示）
interface ComparisonChipProps {
  value: number | null;
  label: string;
  suffix?: string;
  sx?: SxProps<Theme>;
}

const ComparisonChip: React.FC<ComparisonChipProps> = ({ value, label, suffix = "%", sx }) => {
  const chipLabel =
    value !== null
      ? `${label}: ${value > 0 ? "+" : ""}${value.toFixed(1)}${suffix}`
      : `${label}: -`;

  const chipColor =
    value !== null ? (value > 0 ? "success" : value < 0 ? "error" : "default") : "default";

  return <Chip label={chipLabel} color={chipColor} size="small" sx={sx} />;
};

// ダッシュボードホーム画面
const DashboardHome: React.FC = () => {
  return (
    <Box>
      <Typography
        variant="h4"
        component="h1"
        gutterBottom
        sx={{ fontSize: { xs: "1.5rem", sm: "2.125rem" } }}
      >
        ダッシュボード
      </Typography>
      <DashboardContent />
    </Box>
  );
};

// ダッシュボードコンテンツ
const DashboardContent: React.FC = () => {
  const today = getTodayDateString();
  const [selectedPreset, setSelectedPreset] = useState<PeriodPreset>("last4Weeks");
  const [customFrom, setCustomFrom] = useState<string>(today);
  const [customTo, setCustomTo] = useState<string>(today);
  const { from, to } = useMemo(
    () => resolveDateRangeFromPreset(selectedPreset, customFrom, customTo),
    [selectedPreset, customFrom, customTo]
  );

  const { data: todayStats, isLoading: todayLoading, error: todayError } = useDashboardStats();
  const {
    data: periodStats,
    isLoading: periodLoading,
    error: periodError,
  } = usePeriodStats(from, to);

  const handlePresetChange = (event: SelectChangeEvent<PeriodPreset>) => {
    setSelectedPreset(event.target.value as PeriodPreset);
  };

  if (todayLoading) {
    return (
      <Box display="flex" justifyContent="center" py={4}>
        <CircularProgress />
      </Box>
    );
  }

  if (todayError) {
    return <Alert severity="error">統計データの取得に失敗しました</Alert>;
  }

  if (!todayStats) {
    return null;
  }

  // --- 今日 vs 昨日 ---
  const todayResponseRate =
    todayStats.today.uniqueLaunches === 0
      ? null
      : (todayStats.today.totalResponses / todayStats.today.uniqueLaunches) * 100;

  const yesterdayResponseRate =
    todayStats.yesterday.uniqueLaunches === 0
      ? null
      : (todayStats.yesterday.totalResponses / todayStats.yesterday.uniqueLaunches) * 100;

  const todayLaunchesVsYesterday =
    todayStats.yesterday.uniqueLaunches === 0
      ? null
      : ((todayStats.today.uniqueLaunches - todayStats.yesterday.uniqueLaunches) /
          todayStats.yesterday.uniqueLaunches) *
        100;

  const todayResponsesVsYesterday =
    todayStats.yesterday.totalResponses === 0
      ? null
      : ((todayStats.today.totalResponses - todayStats.yesterday.totalResponses) /
          todayStats.yesterday.totalResponses) *
        100;

  const todayResponseRateVsYesterday =
    todayResponseRate !== null && yesterdayResponseRate !== null
      ? todayResponseRate - yesterdayResponseRate
      : null;

  // 問題別統計: 期間プリセットに連動
  const selectedTopicData = periodStats?.total.byTopic ?? {};

  return (
    <Box>
      {/* 今日の概要 */}
      <Typography variant="h6" gutterBottom>
        今日の概要
      </Typography>
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid size={{ xs: 12, sm: 4 }}>
          <Card>
            <CardContent>
              <Typography color="text.secondary" gutterBottom>
                起動数
              </Typography>
              <Typography variant="h3">
                {todayStats.today.uniqueLaunches.toLocaleString()}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                総起動: {todayStats.today.totalLaunches.toLocaleString()}回
              </Typography>
              <ComparisonChip value={todayLaunchesVsYesterday} label="昨日比" sx={{ mt: 1 }} />
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                昨日: {todayStats.yesterday.uniqueLaunches.toLocaleString()}人
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, sm: 4 }}>
          <Card>
            <CardContent>
              <Typography color="text.secondary" gutterBottom>
                回答数
              </Typography>
              <Typography variant="h3">
                {todayStats.today.totalResponses.toLocaleString()}
              </Typography>
              <ComparisonChip value={todayResponsesVsYesterday} label="昨日比" sx={{ mt: 1 }} />
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                昨日: {todayStats.yesterday.totalResponses.toLocaleString()}件
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, sm: 4 }}>
          <Card>
            <CardContent>
              <Typography color="text.secondary" gutterBottom>
                回答率
              </Typography>
              <Typography variant="h3">
                {todayResponseRate !== null ? `${todayResponseRate.toFixed(1)}%` : "-"}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {todayStats.today.totalResponses.toLocaleString()}回答 /{" "}
                {todayStats.today.uniqueLaunches.toLocaleString()}起動
              </Typography>
              <ComparisonChip
                value={todayResponseRateVsYesterday}
                label="昨日比"
                suffix="pt"
                sx={{ mt: 1 }}
              />
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                昨日:{" "}
                {yesterdayResponseRate !== null ? `${yesterdayResponseRate.toFixed(1)}%` : "-"}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* 期間セレクター */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 2,
          mb: 3,
          flexWrap: "wrap",
        }}
      >
        <Typography variant="h6">期間別統計</Typography>
        <FormControl size="small">
          <Select<PeriodPreset> value={selectedPreset} onChange={handlePresetChange}>
            {PERIOD_PRESET_OPTIONS.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        {selectedPreset === "custom" && (
          <>
            <TextField
              type="date"
              size="small"
              label="開始"
              value={customFrom}
              onChange={(event) => setCustomFrom(event.target.value)}
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              type="date"
              size="small"
              label="終了"
              value={customTo}
              onChange={(event) => setCustomTo(event.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </>
        )}
        <Typography variant="body2" color="text.secondary">
          {from} 〜 {to}
        </Typography>
      </Box>

      {/* 期間別セクション */}
      {periodLoading ? (
        <Box display="flex" justifyContent="center" py={4}>
          <CircularProgress />
        </Box>
      ) : periodError ? (
        <Alert severity="error" sx={{ mb: 3 }}>
          期間データの取得に失敗しました
        </Alert>
      ) : periodStats ? (
        <>
          {/* 週別サマリーテーブル */}
          <Card sx={{ mb: 4 }}>
            <CardContent>
              <Typography variant="subtitle1" gutterBottom fontWeight="bold">
                週別サマリー
              </Typography>
              <Box sx={{ overflowX: "auto" }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>期間</TableCell>
                      <TableCell align="right">起動数</TableCell>
                      <TableCell align="right">回答数</TableCell>
                      <TableCell align="right">回答率</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {periodStats.weeks.map((week) => {
                      const responseRate =
                        week.stats.uniqueLaunches === 0
                          ? null
                          : (week.stats.totalResponses / week.stats.uniqueLaunches) * 100;
                      return (
                        <TableRow key={week.startDate}>
                          <TableCell>{week.weekLabel}</TableCell>
                          <TableCell align="right">
                            {week.stats.uniqueLaunches.toLocaleString()}
                          </TableCell>
                          <TableCell align="right">
                            {week.stats.totalResponses.toLocaleString()}
                          </TableCell>
                          <TableCell align="right">
                            {responseRate !== null ? `${responseRate.toFixed(1)}%` : "-"}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                    <TableRow sx={{ backgroundColor: "action.hover" }}>
                      <TableCell sx={{ fontWeight: "bold" }}>合計</TableCell>
                      <TableCell align="right" sx={{ fontWeight: "bold" }}>
                        {periodStats.total.uniqueLaunches.toLocaleString()}
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: "bold" }}>
                        {periodStats.total.totalResponses.toLocaleString()}
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: "bold" }}>
                        {periodStats.total.uniqueLaunches === 0
                          ? "-"
                          : `${((periodStats.total.totalResponses / periodStats.total.uniqueLaunches) * 100).toFixed(1)}%`}
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </Box>
            </CardContent>
          </Card>

          {/* 問題別統計 */}
          <Card sx={{ mb: 4 }}>
            <CardContent>
              <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 2 }}>
                問題別統計
              </Typography>
              <TopicStatsTableComponent data={selectedTopicData} />
            </CardContent>
          </Card>
        </>
      ) : null}

      {/* 回答数推移グラフ */}
      <Box sx={{ mt: 3 }}>
        <ResponseStatsChart />
      </Box>
    </Box>
  );
};

// 問題別回答数テーブル
interface TopicStatsTableProps {
  data: { [topicId: string]: TopicStats };
}

const TopicStatsTableComponent: React.FC<TopicStatsTableProps> = ({ data }) => {
  if (!data || typeof data !== "object") {
    return (
      <Typography variant="body2" color="text.secondary">
        データがありません
      </Typography>
    );
  }

  const dataKeys = Object.keys(data);

  if (dataKeys.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary">
        データがありません
      </Typography>
    );
  }

  // 回答数でソート（降順）、同数なら閲覧数でソート
  const sortedEntries = dataKeys
    .map((id) => ({ id, ...data[id] }))
    .sort((a, b) => b.count - a.count || (b.viewCount || 0) - (a.viewCount || 0));

  // 総合計
  const grandTotalViews = sortedEntries.reduce((sum, entry) => sum + (entry.viewCount || 0), 0);
  const grandTotalResponses = sortedEntries.reduce((sum, entry) => sum + entry.count, 0);

  return (
    <Box sx={{ overflowX: "auto", width: "100%", maxHeight: 400, overflow: "auto" }}>
      <Table size="small" stickyHeader>
        <TableHead>
          <TableRow>
            <TableCell>問題</TableCell>
            <TableCell align="right">閲覧</TableCell>
            <TableCell align="right">回答</TableCell>
            <TableCell align="right">回答率</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {sortedEntries.map((entry) => {
            const viewCount = entry.viewCount || 0;
            const responseRate = viewCount > 0 ? (entry.count / viewCount) * 100 : null;
            return (
              <TableRow key={entry.id}>
                <TableCell title={entry.contentPreview}>
                  <Box>
                    <TopicBadges
                      difficulty={entry.difficulty}
                      genre={entry.genre}
                      sx={{ mb: 0.5 }}
                    />
                    {entry.theme && (
                      <Typography variant="body2" fontWeight="bold" sx={{ mt: 0.5 }}>
                        {entry.theme}
                      </Typography>
                    )}
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{
                        mt: 0.5,
                        maxWidth: 300,
                        display: "-webkit-box",
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: "vertical",
                        overflow: "hidden",
                      }}
                    >
                      {entry.contentPreview}
                    </Typography>
                  </Box>
                </TableCell>
                <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>
                  {viewCount.toLocaleString()}
                </TableCell>
                <TableCell align="right" sx={{ fontWeight: "bold", whiteSpace: "nowrap" }}>
                  {entry.count.toLocaleString()}
                </TableCell>
                <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>
                  {responseRate !== null ? `${responseRate.toFixed(1)}%` : "-"}
                </TableCell>
              </TableRow>
            );
          })}
          <TableRow sx={{ backgroundColor: "action.hover" }}>
            <TableCell sx={{ fontWeight: "bold" }}>合計</TableCell>
            <TableCell align="right" sx={{ fontWeight: "bold" }}>
              {grandTotalViews.toLocaleString()}
            </TableCell>
            <TableCell align="right" sx={{ fontWeight: "bold" }}>
              {grandTotalResponses.toLocaleString()}
            </TableCell>
            <TableCell align="right" sx={{ fontWeight: "bold" }}>
              {grandTotalViews > 0
                ? `${((grandTotalResponses / grandTotalViews) * 100).toFixed(1)}%`
                : "-"}
            </TableCell>
          </TableRow>
        </TableBody>
      </Table>
    </Box>
  );
};

export default DashboardHome;
